import React, { useState } from 'react';
import { FaCalendarAlt, FaTimes } from 'react-icons/fa';
import Bouton from '../Bouton/Bouton';
import { checkMot } from '../../../ressources/functionJS/checkMot';
import * as s from '../../../ressources/StyleGlobal/StyleGlobal';

interface CalendrierSimpleProps {
  onPeriodChange?: (dateDebut: Date | null, dateFin: Date | null) => void;
  titre?: string;
  dateDebutDefaut?: Date;
  dateFinDefaut?: Date;
  // NOUVEAU : Dates à bloquer (déjà utilisées par d'autres sélections)
  datesBloquees?: Date[];
  // NOUVEAU : Permettre de sélectionner des dates déjà bloquées
  autoriserDatesBloquees?: boolean;
}

const CalendrierSimpleAvecBlocage: React.FC<CalendrierSimpleProps> = ({
  onPeriodChange,
  titre = "Sélectionner une période",
  dateDebutDefaut,
  dateFinDefaut,
  datesBloquees = [],
  autoriserDatesBloquees = false,
}) => {
  const [showPopup, setShowPopup] = useState(false);
  const [dateDebut, setDateDebut] = useState<Date | null>(dateDebutDefaut || null);
  const [dateFin, setDateFin] = useState<Date | null>(dateFinDefaut || null);
  const [selectionMode, setSelectionMode] = useState<'debut' | 'fin' | null>(null);
  const [messageErreur, setMessageErreur] = useState<string>('');
  
  // Génération du calendrier du mois actuel
  const aujourdhui = new Date();
  const anneeActuelle = aujourdhui.getFullYear();
  const moisActuel = aujourdhui.getMonth();
  
  const [anneeAffichee, setAnneeAffichee] = useState(anneeActuelle);
  const [moisAffiche, setMoisAffiche] = useState(moisActuel);

  const premiersJourMois = new Date(anneeAffichee, moisAffiche, 1);
  const dernierJourMois = new Date(anneeAffichee, moisAffiche + 1, 0);
  const premierJourSemaine = premiersJourMois.getDay();
  const nombreJours = dernierJourMois.getDate();

  const nomsJours = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
  const nomsMois = [
    'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun',
    'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'
  ];

  // NOUVEAU : Vérifier si une date est bloquée
  const estDateBloquee = (date: Date): boolean => {
    return datesBloquees.some(dateBloquee => 
      dateBloquee.toDateString() === date.toDateString()
    );
  };

  // NOUVEAU : Vérifier si la plage contient des dates bloquées
  const plageContientDatesBloquees = (debut: Date, fin: Date): boolean => {
    const dateDebut = new Date(debut);
    const dateFin = new Date(fin);
    
    // Parcourir toutes les dates entre début et fin
    for (let d = new Date(dateDebut); d <= dateFin; d.setDate(d.getDate() + 1)) {
      if (estDateBloquee(new Date(d))) {
        return true;
      }
    }
    return false;
  };

  const changerMois = (direction: number) => {
    let nouveauMois = moisAffiche + direction;
    let nouvelleAnnee = anneeAffichee;

    if (nouveauMois < 0) {
      nouveauMois = 11;
      nouvelleAnnee--;
    } else if (nouveauMois > 11) {
      nouveauMois = 0;
      nouvelleAnnee++;
    }

    setMoisAffiche(nouveauMois);
    setAnneeAffichee(nouvelleAnnee);
  };

  const selectionnerDate = (jour: number) => {
    const dateSelectionnee = new Date(anneeAffichee, moisAffiche, jour);
    
    // NOUVEAU : Vérifier si la date est bloquée
    if (!autoriserDatesBloquees && estDateBloquee(dateSelectionnee)) {
      setMessageErreur('❌ Cette date est déjà utilisée !');
      setTimeout(() => setMessageErreur(''), 3000);
      return;
    }

    if (!selectionMode) {
      // Première sélection = début
      setDateDebut(dateSelectionnee);
      setDateFin(null);
      setSelectionMode('fin');
      setMessageErreur('');
    } else if (selectionMode === 'fin') {
      // Deuxième sélection = fin
      let debut = dateDebut;
      let fin = dateSelectionnee;
      
      if (dateDebut && dateSelectionnee < dateDebut) {
        // Si la date fin est avant le début, on inverse
        debut = dateSelectionnee;
        fin = dateDebut;
      }

      // NOUVEAU : Vérifier si la plage contient des dates bloquées
      if (!autoriserDatesBloquees && debut && fin && plageContientDatesBloquees(debut, fin)) {
        setMessageErreur('❌ La période sélectionnée contient des dates déjà utilisées !');
        setTimeout(() => setMessageErreur(''), 3000);
        return;
      }

      setDateDebut(debut);
      setDateFin(fin);
      setSelectionMode(null);
      setMessageErreur('');
      
      // Appeler le callback et fermer le popup
      if (onPeriodChange) {
        onPeriodChange(debut, fin);
      }
      setShowPopup(false);
    }
  };

  const reinitialiser = () => {
    setDateDebut(null);
    setDateFin(null);
    setSelectionMode(null);
    setMessageErreur('');
    if (onPeriodChange) {
      onPeriodChange(null, null);
    }
  };

  const estDateSelectionnee = (jour: number) => {
    const dateJour = new Date(anneeAffichee, moisAffiche, jour);
    
    if (dateDebut && dateJour.toDateString() === dateDebut.toDateString()) return 'debut';
    if (dateFin && dateJour.toDateString() === dateFin.toDateString()) return 'fin';
    
    // Vérifier si la date est dans la plage
    if (dateDebut && dateFin) {
      if (dateJour > dateDebut && dateJour < dateFin) return 'plage';
    }
    
    return null;
  };

  const genererJoursCalendrier = () => {
    const jours = [];
    
    // Jours vides au début
    for (let i = 0; i < premierJourSemaine; i++) {
      jours.push(
        <div key={`vide-${i}`} style={{ padding: '4px' }}></div>
      );
    }

    // Jours du mois
    for (let jour = 1; jour <= nombreJours; jour++) {
      const typeSelection = estDateSelectionnee(jour);
      const dateJour = new Date(anneeAffichee, moisAffiche, jour);
      const estAujourdhui = jour === aujourdhui.getDate() && 
                           moisAffiche === aujourdhui.getMonth() && 
                           anneeAffichee === aujourdhui.getFullYear();
      
      // NOUVEAU : Vérifier si la date est bloquée
      const dateBloquee = estDateBloquee(dateJour);

      let styleJour: React.CSSProperties = {
        padding: '4px',
        margin: '1px',
        textAlign: 'center',
        cursor: dateBloquee && !autoriserDatesBloquees ? 'not-allowed' : 'pointer',
        borderRadius: '3px',
        border: '1px solid #ced4da',
        backgroundColor: '#fff',
        minHeight: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '11px',
        fontWeight: '400',
        position: 'relative',
      };

      // NOUVEAU : Style pour les dates bloquées
      if (dateBloquee && !autoriserDatesBloquees) {
        styleJour = { 
          ...styleJour, 
          backgroundColor: '#f8d7da', 
          border: '1px solid #dc3545',
          color: '#721c24',
          opacity: 0.6,
          textDecoration: 'line-through'
        };
      }

      if (estAujourdhui && !dateBloquee) {
        styleJour = { ...styleJour, backgroundColor: '#fff3cd', border: '1px solid #ffc107', fontWeight: 'bold' };
      }
      if (typeSelection === 'debut') {
        styleJour = { ...styleJour, backgroundColor: '#28a745', color: 'white', fontWeight: 'bold' };
      }
      if (typeSelection === 'fin') {
        styleJour = { ...styleJour, backgroundColor: '#dc3545', color: 'white', fontWeight: 'bold' };
      }
      if (typeSelection === 'plage') {
        styleJour = { ...styleJour, backgroundColor: '#d4edda', border: '1px solid #28a745' };
      }

      jours.push(
        <div
          key={jour}
          style={styleJour}
          onClick={() => selectionnerDate(jour)}
          onMouseEnter={(e) => {
            if (typeSelection === null && !(dateBloquee && !autoriserDatesBloquees)) {
              e.currentTarget.style.backgroundColor = '#e9ecef';
            }
          }}
          onMouseLeave={(e) => {
            if (typeSelection === null && !(dateBloquee && !autoriserDatesBloquees)) {
              e.currentTarget.style.backgroundColor = dateBloquee ? '#f8d7da' : '#fff';
            }
          }}
          title={dateBloquee ? 'Date déjà utilisée' : ''}
        >
          {jour}
        </div>
      );
    }

    return jours;
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      {/* Bouton pour ouvrir le calendrier */}
      <Bouton cliquer={() => setShowPopup(!showPopup)}>
        <FaCalendarAlt /> 
        {titre}
      </Bouton>

      {/* Affichage compact de la période sélectionnée */}
      {(dateDebut || dateFin) && (
        <div style={{
          fontSize: '10px',
          color: '#6c757d',
          textAlign: 'center',
          marginTop: '2px',
          padding: '2px 5px',
          backgroundColor: '#f8f9fa',
          borderRadius: '3px',
          border: '1px solid #dee2e6',
          minWidth: '120px'
        }}>
          {dateDebut && dateFin ? (
            <>
              📅 {dateDebut.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} 
              {' → '}
              {dateFin.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}
            </>
          ) : dateDebut ? (
            <>📅 Début: {dateDebut.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}</>
          ) : (
            <>📅 Fin: {dateFin?.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}</>
          )}
        </div>
      )}

      {/* Popup du calendrier */}
      {showPopup && (
        <>
          {/* Overlay pour fermer en cliquant à l'extérieur */}
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999,
            }}
            onClick={() => setShowPopup(false)}
          />
          
          {/* Calendrier compact */}
          <div 
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              zIndex: 1000,
              backgroundColor: 'white',
              border: '1px solid #ced4da',
              borderRadius: '5px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
              padding: '10px',
              minWidth: '250px',
              marginTop: '5px'
            }}
          >
            {/* En-tête compact */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '10px',
              borderBottom: '1px solid #dee2e6',
              paddingBottom: '8px'
            }}>
              <button 
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '14px',
                  cursor: 'pointer',
                  color: '#007bff',
                  fontWeight: 'bold'
                }}
                onClick={() => changerMois(-1)}
              >
                ‹
              </button>
              <span style={{ fontWeight: 'bold', fontSize: '12px' }}>
                {nomsMois[moisAffiche]} {anneeAffichee}
              </span>
              <button 
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '14px',
                  cursor: 'pointer',
                  color: '#007bff',
                  fontWeight: 'bold'
                }}
                onClick={() => changerMois(1)}
              >
                ›
              </button>
            </div>

            {/* NOUVEAU : Message d'erreur */}
            {messageErreur && (
              <div style={{
                fontSize: '10px',
                color: '#721c24',
                backgroundColor: '#f8d7da',
                border: '1px solid #f5c6cb',
                borderRadius: '3px',
                padding: '5px',
                marginBottom: '8px',
                textAlign: 'center'
              }}>
                {messageErreur}
              </div>
            )}

            {/* Instructions compactes */}
            <div style={{ 
              fontSize: '10px', 
              color: '#6c757d', 
              marginBottom: '8px',
              textAlign: 'center'
            }}>
              {!dateDebut && "Cliquez pour début"}
              {dateDebut && !dateFin && "Cliquez pour fin"}
              {dateDebut && dateFin && "Période sélectionnée"}
            </div>

            {/* En-têtes des jours compacts */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(7, 1fr)', 
              gap: '1px', 
              marginBottom: '5px' 
            }}>
              {nomsJours.map(jour => (
                <div key={jour} style={{ 
                  textAlign: 'center', 
                  fontWeight: 'bold', 
                  color: '#6c757d', 
                  fontSize: '9px',
                  padding: '2px'
                }}>
                  {jour}
                </div>
              ))}
            </div>

            {/* Grille du calendrier compacte */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(7, 1fr)', 
              gap: '1px', 
              marginBottom: '10px' 
            }}>
              {genererJoursCalendrier()}
            </div>

            {/* NOUVEAU : Légende */}
            <div style={{
              fontSize: '9px',
              color: '#6c757d',
              marginBottom: '10px',
              display: 'flex',
              justifyContent: 'space-around',
              flexWrap: 'wrap',
              gap: '5px'
            }}>
              <span>🟢 Début</span>
              <span>🔴 Fin</span>
              <span>🟩 Plage</span>
              <span style={{ textDecoration: 'line-through' }}>❌ Bloqué</span>
            </div>

            {/* Boutons d'action compacts */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              gap: '5px',
              borderTop: '1px solid #dee2e6',
              paddingTop: '8px'
            }}>
              <button
                style={{
                  padding: '4px 8px',
                  fontSize: '10px',
                  border: '1px solid #ced4da',
                  borderRadius: '3px',
                  backgroundColor: '#f8f9fa',
                  cursor: 'pointer'
                }}
                onClick={reinitialiser}
              >
                Effacer
              </button>
              <button
                style={{
                  padding: '4px 8px',
                  fontSize: '10px',
                  border: '1px solid #ced4da',
                  borderRadius: '3px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  cursor: 'pointer'
                }}
                onClick={() => setShowPopup(false)}
              >
                Fermer
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CalendrierSimpleAvecBlocage;
